import { getPointsBalance } from "api";
import { ILoggedCustomer, IReward } from "api/types";
import { ApolloClient } from "apollo-boost";
import { AppStateContext, MutateState, useAppState } from "AppState";
import Container from "components/common/Grid/Container";
import PanelLayout from "components/common/PanelLayout/PanelLayout";
import { ConfigurationLoaderContext } from "components/Gates/ConfigurationLoaderGate";
import { CustomerDataContext } from "components/Gates/CustomerDataGate";
import { History } from "history";
import * as _ from "lodash";
import * as React from "react";
import { ApolloConsumer } from "react-apollo";
import { Col, Row } from "react-grid-system";
import { getCurrentLocation, getRewards } from "utils/apis";
import Cart from "./ChooseRewards/Cart/Cart";
import RewardsList from "./ChooseRewards/RewardsList/RewardsList";

export interface IProps {
  history: History;
  pointsBalance: number;
  rewards: IReward[];
  cartRewards?: IReward[];
  locationName?: string;
  setPointsBalanceInAppState?(pointsBalance: number): void;
}

export interface ITabletLocation {
  name: string;
  locationId: string;
  tenantId: string;
}

export interface IState {
  pointsBalance: number;
  rewards: IReward[];
  cartRewards: IReward[];
  total: number;
  tenantId: string;
  locationId: string;
}

const ChooseRewardsWrapper = ({
  ...props
}: IProps & {
  client: ApolloClient<any>;
  loggedCustomer: ILoggedCustomer | undefined;
}): JSX.Element => {
  const { locationName, setPointsBalance, pointsBalance } = useAppState();
  return (
    <ChooseRewards
      {...props}
      locationName={locationName}
      setPointsBalanceInAppState={setPointsBalance}
      pointsBalance={pointsBalance}
    />
  );
};

class ChooseRewards extends React.Component<
  IProps & {
    client: ApolloClient<any>;
    loggedCustomer: ILoggedCustomer | undefined;
  },
  IState
> {
  constructor(
    props: IProps & {
      client: ApolloClient<any>;
      loggedCustomer: ILoggedCustomer | undefined;
    }
  ) {
    super(props);
    this.state = {
      pointsBalance: this.props.pointsBalance || 0,
      rewards: [],
      cartRewards: this.props.cartRewards || [],
      total: 0,
      locationId: "",
      tenantId: ""
    };
  }

  public async componentDidMount() {
    await this.getCurrentUser();
    await this.getRewards();
    await this.getPointsBalance();
  }

  public render() {
    return (
      <ConfigurationLoaderContext.Consumer>
        {config => (
          <>
            {config && (
              <PanelLayout
                title={this.props.locationName}
                titleSecondary="Redeem Rewards"
                backButtonHandler={this.props.history.goBack}
              >
                <Container style={{ width: "95%" }}>
                  <Row style={{ position: "relative", height: "100%" }}>
                    <Col
                      sm={7}
                      lg={8}
                      style={{
                        overflowY: "auto",
                        overflowX: "hidden",
                        height: "100%",
                        position: "relative"
                      }}
                    >
                      <div style={{ position: "absolute", width: "100%" }}>
                        <RewardsList
                          rewards={this.state.rewards}
                          addToCart={this.addReward}
                          pointsBalance={this.state.pointsBalance}
                          tenantId={this.state.tenantId}
                          locationId={this.state.locationId}
                          imageBaseUrl={config.reward_images_base_url}
                        />
                      </div>
                    </Col>
                    <Col sm={5} lg={4}>
                      <Col md={12}>
                        <Cart
                          rewards={this.state.cartRewards}
                          pointsBalance={this.state.pointsBalance}
                          total={this.state.total}
                          removeFromCart={this.removeReward}
                          rewardImageBaseUrl={config.reward_images_base_url}
                          tenantId={this.state.tenantId}
                          locationId={this.state.locationId}
                        />
                      </Col>
                    </Col>
                  </Row>
                </Container>
              </PanelLayout>
            )}
          </>
        )}
      </ConfigurationLoaderContext.Consumer>
    );
  }

  private getPointsBalance = async () => {
    try {
      const pointsBalance: number = await getPointsBalance(
        this.props.client,
        this.props.loggedCustomer ? this.props.loggedCustomer.authToken : ""
      );
      this.setState({ pointsBalance });
      if (this.props.setPointsBalanceInAppState) {
        this.props.setPointsBalanceInAppState(pointsBalance);
      }
    } catch (err) {
      console.error("error while retreiving points", err);
    }
  };

  // @TODO when endpoints are ready, get pointsBalance from props (initial page  load)
  private initializeAmount = (rewards: IReward[]): any => {
    return rewards.map(reward => ({
      id: reward.id,
      pointsRequired: reward.pointsRequired,
      name: reward.name,
      defaultImage: "/images/reward.gif",
      quantity: 0,
      added: false,
      imageId: reward.imageId
    }));
  };

  private addReward = (reward: IReward) => {
    const { pointsRequired, added, id }: any = reward;
    const pointsBalance = this.state.pointsBalance;
    if (pointsRequired <= pointsBalance) {
      if (!added) {
        this.addToCart(id);
      }
      this.increaseQuantity(id);
      this.subtractPoints(id);
    }
  };

  private addToCart = (rewardId: number): void => {
    this.setState(state => {
      const addedReward = this.setAddedStatusTo(true, state.rewards, rewardId);
      const cartRewards = state.cartRewards.concat(addedReward);
      return { cartRewards };
    });
  };

  private increaseQuantity = (rewardId: number): void => {
    this.setState(state => {
      const cartRewards = state.cartRewards.map((reward: IReward) => {
        if (reward.id === rewardId) {
          // @ts-ignore
          reward.quantity = reward.quantity + 1;
        }
        return reward;
      });
      return { cartRewards };
    });
  };

  private subtractPoints = (rewardId: number): void => {
    this.setState(state => {
      const reward = this.getReward(state.cartRewards, rewardId);
      const total = this.calculateTotal(state.cartRewards);
      const pointsBalance = state.pointsBalance - reward.pointsRequired;
      return { total, pointsBalance };
    });
  };

  private removeReward = (reward: IReward): void => {
    const { id, added, quantity }: any = reward;
    if (added) {
      if (quantity > 0) {
        this.decreaseQuantity(id);
      }
      this.addPoints(id);
    }
  };

  private removeFromCart = (rewardId: number): void => {
    this.setState((state): any => {
      const reward = this.getReward(state.cartRewards, rewardId);
      if (reward.quantity === 0) {
        const cartWithoutReward = _.without(state.cartRewards, reward);
        this.setAddedStatusTo(false, state.rewards, rewardId);
        return { cartRewards: cartWithoutReward };
      }
    });
  };

  private decreaseQuantity = (rewardId: number): void => {
    this.setState(
      (state): any => {
        const cartRewards = state.cartRewards.map((reward: IReward): any => {
          const quantity: any = reward.quantity;
          if (reward.id === rewardId) {
            // @ts-ignore
            reward.quantity = reward.quantity - 1;
          }
          return reward;
        });
        return { cartRewards };
      },
      () => this.removeFromCart(rewardId)
    );
  };

  private addPoints = (rewardId: number): void => {
    this.setState((state): any => {
      const reward = this.getReward(state.cartRewards, rewardId);
      console.log("addPoints ", reward.quantity);
      if (reward.quantity >= 0) {
        const total = this.calculateTotal(state.cartRewards);
        const pointsBalance = state.pointsBalance + reward.pointsRequired;
        return { total, pointsBalance };
      }
    });
  };

  private calculateTotal = (rewardsInCart: IReward[]): number => {
    let total = 0;
    rewardsInCart.forEach(reward => {
      const { quantity }: any = reward;
      const { pointsRequired }: any = reward;
      total += pointsRequired * quantity;
    });
    return total;
  };

  private getReward = (cartRewards: IReward[], rewardId: number): any =>
    _.find(cartRewards, reward => reward.id === rewardId);

  private setAddedStatusTo = (
    newStatus: boolean,
    cartRewards: IReward[],
    rewardId: number
  ): IReward =>
    _.find(cartRewards, (reward: any) => {
      if (reward.id === rewardId) {
        reward.added = newStatus;
        return true;
      }
      return false;
    });

  private getRewards = async () => {
    try {
      const rewardsList: any = await getRewards(this.props.client);
      if (rewardsList) {
        const rewards = this.initializeAmount(rewardsList);
        this.setState({ rewards });
      }
    } catch (e) {
      console.error(e);
    }
  };

  private getCurrentUser = async () => {
    try {
      const location = await getCurrentLocation(this.props.client);
      this.setState({
        tenantId: location.tenantId,
        locationId: location.locationId
      });
    } catch (e) {
      console.error(e);
    }
  };
}

export default (props: IProps) => (
  <ApolloConsumer>
    {client => (
      <CustomerDataContext.Consumer>
        {loggedCustomer => (
          <ChooseRewardsWrapper
            client={client}
            loggedCustomer={loggedCustomer}
            {...props}
          />
        )}
      </CustomerDataContext.Consumer>
    )}
  </ApolloConsumer>
);



// WEBPACK FOOTER //
// ./src/screens/RedeemRewards/index.tsx